import React, { useState } from 'react';
import {
  Dialog,
  Button,
  TextField,
  Grid,
  IconButton,
  Box,
  Typography,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AuthorizationClient from '../single-page/api/AuthorizationClient.js';
import grafismo from '../../assets/images/grafismo.svg';
import { useModals } from '@/hooks/useModals.jsx';

const inputStyles = {
  marginBottom: '12px',
  width: {
    xs: '95%',
    sm: '90%',
    md: '80%'
  },
  '& .MuiOutlinedInput-root': {
    border: '1.68px solid #012F4740',
    borderRadius: '8px',
    transition: 'all 0.2s ease-in-out',
    '& fieldset': {
      border: 'none'
    },
    '&:hover': {
      borderColor: '#012F47'
    },
    '&.Mui-focused': {
      borderColor: '#012F47'
    }
  },
  '& .MuiInputLabel-root': {
    color: '#666',
    '&.Mui-focused': {
      color: '#012F47'
    }
  },
  '& .MuiInputLabel-outlined': {
    '&.MuiInputLabel-shrink': {
      background: 'white',
      padding: '0 8px',
      marginLeft: '-4px',
      transform: 'translate(14px, -9px) scale(0.75)'
    }
  }
};

const ForgotPasswordModal = () => {
  const { 
    forgotPasswordOpen, 
    handleCloseForgotPassword,
    handleOpenLogin 
  } = useModals();

  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      await AuthorizationClient.requestPasswordReset({ email });
      setSuccessMessage('E-mail de recuperação enviado com sucesso! Verifique sua caixa de entrada.');
      setTimeout(() => {
        handleCloseForgotPassword();
        handleOpenLogin();
      }, 3000);
    } catch (error) {
      setErrorMessage(error.data?.error || 'Ocorreu um erro. Por favor, tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog 
      open={forgotPasswordOpen}
      onClose={handleCloseForgotPassword}
      maxWidth="md" 
      fullWidth
      PaperProps={{
        style: { 
          borderRadius: 16,
          margin: '16px',
          position: 'relative',
          borderTop: '4px solid #51DF94',
          boxShadow: '0 24px 48px rgba(0,0,0,0.35)',
        }
      }}
    >
      <Grid container>
        <Grid 
          item 
          xs={2}
          sm={3}
          md={4}
          sx={{ 
            backgroundColor: '#012F47',
            minHeight: { 
              xs: '150px',
              sm: '300px',
              md: '400px'
            },
            position: 'relative',
            display: 'block'
          }}
        >
          <Box
            component="img"
            src={grafismo}
            alt="Grafismo"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              objectFit: 'contain'
            }}
          />
        </Grid>

        <Grid 
          item 
          xs={10}
          sm={9}
          md={8}
          style={{ position: 'relative' }}
        >
          <IconButton
            onClick={handleCloseForgotPassword}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500'
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ 
            p: { 
              xs: 2,
              sm: 3,
              md: 4  
            },
            pt: { 
              xs: 4,
              sm: 5 
            },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            )}

            {successMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMessage}
              </Alert>
            )}

            <Typography 
              variant="h4" 
              sx={{ 
                mb: 3,
                textAlign: 'center',
                fontWeight: 600,
                fontSize: {
                  xs: '1.5rem',
                  sm: '2rem'
                }
              }}
            >
              Esqueceu sua senha?
            </Typography>

            <Typography
              sx={{
                mb: 4,
                textAlign: 'center',
                color: '#666',
                width: { xs: '95%', sm: '90%', md: '80%' }
              }}
            >
              Digite seu e-mail cadastrado e enviaremos instruções para redefinir sua senha.
            </Typography>

            <form 
              onSubmit={handleSubmit} 
              style={{ 
                width: '100%', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center' 
              }}
            >
              <TextField
                name="email"
                label="E-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                sx={inputStyles}
                type="email"
              />

              <Box sx={{ 
                mt: 3,
                padding: 0,
                display: 'flex',
                justifyContent: 'center',
                gap: { xs: 1, sm: 2 },
                width: { xs: '95%', sm: '90%', md: '80%' },
                flexDirection: { xs: 'column', sm: 'row' }
              }}>
                <Button 
                  type="submit"
                  variant="contained"
                  disabled={isLoading}
                  sx={{ 
                    bgcolor: '#4CAF50',
                    padding: { xs: '10px 20px', sm: '12px 40px' },
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    textTransform: 'none',
                    borderRadius: '6px',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      bgcolor: '#388E3C',
                    }
                  }}
                >
                  {isLoading ? 'Enviando...' : 'Enviar'}
                </Button>

                <Button 
                  type="button"
                  onClick={() => {
                    handleCloseForgotPassword();
                    handleOpenLogin();
                  }}
                  variant="outlined"
                  disabled={isLoading}
                  sx={{ 
                    color: '#012F47',
                    borderColor: '#012F47',
                    padding: { xs: '10px 20px', sm: '12px 40px' },
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    textTransform: 'none',
                    borderRadius: '6px',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      borderColor: '#012F47',
                      backgroundColor: 'rgba(1, 47, 71, 0.04)'
                    }
                  }}
                >
                  Voltar ao login
                </Button>
              </Box>
            </form>
          </Box>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default ForgotPasswordModal;